import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SectionLabel from "./SectionLabel";
import { FadeUp, MaskLine } from "./Reveal";

const fields = [
  { label: "MY CURRENT POSITION", hint: "What is true right now?" },
  { label: "MY INTELLIGENCE GAPS", hint: "What don't I know yet?" },
  { label: "INTELLIGENCE RECEIVED", hint: "From Heaven, the room, the conversations, the experience." },
  { label: "THE ROOM", hint: "Who did I meet? What intelligence did they carry?" },
  { label: "FURTHER INVESTIGATION", hint: "What requires more prayer, research, conversation, or discernment?" },
  { label: "MY NEXT MOVE", hint: "What will I do with what I now know?" },
];

export default function FieldGuide() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [22, 4, -6]);
  const y = useTransform(scrollYProgress, [0, 1], [90, -50]);

  return (
    <section ref={ref} data-testid="field-guide-section" className="relative overflow-hidden border-b border-ink/15">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
        <SectionLabel testid="field-guide-label">Your Intelligence Document</SectionLabel>

        <div className="grid items-center gap-16 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">
              <MaskLine>You will not leave with</MaskLine>
              <MaskLine delay={0.1}>
                <span className="italic text-wine">random notes.</span>
              </MaskLine>
            </h2>
            <FadeUp delay={0.2} className="mt-8 max-w-md space-y-6 text-base leading-relaxed text-smoke md:text-lg">
              <p>
                Every attendee will receive the 2026 RECON Field Guide — a guided intelligence
                document designed to help you capture what matters.
              </p>
              <p className="font-sans text-[11px] tracking-[0.3em] text-oxblood">
                INSIDE, YOU WILL EXPLORE:
              </p>
              <ul className="space-y-3">
                {fields.map((f) => (
                  <li key={f.label} className="flex items-baseline gap-4 font-sans text-xs tracking-[0.15em] text-ink/80">
                    <span className="text-gold">+</span>
                    {f.label}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>

          <div style={{ perspective: 1200 }}>
            <motion.div
              data-testid="field-guide-dossier"
              style={{ rotateX, y, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1 }}
              className="relative mx-auto max-w-md -rotate-2 border border-ink/30 bg-[#FFFDF8] p-8 shadow-[0_50px_90px_-40px_rgba(26,26,26,0.5)] md:p-10"
            >
              <div className="flex items-start justify-between border-b-2 border-ink pb-5">
                <div>
                  <p className="font-sans text-[9px] tracking-[0.35em] text-ink/50">
                    WMWE // INTELLIGENCE DIVISION
                  </p>
                  <h3 className="mt-2 font-serif text-2xl font-medium tracking-tight text-ink">
                    2026 RECON Field Guide
                  </h3>
                </div>
                <span className="rotate-6 border-2 border-oxblood px-2 py-1 font-sans text-[8px] font-semibold tracking-[0.25em] text-oxblood">
                  PERSONAL COPY
                </span>
              </div>

              <div className="mt-6 space-y-6">
                {fields.map((f, i) => (
                  <div key={f.label}>
                    <p className="font-sans text-[10px] font-semibold tracking-[0.25em] text-ink">
                      {String(i + 1).padStart(2, "0")} — {f.label}
                    </p>
                    <p className="mt-1 font-serif text-sm italic text-smoke">{f.hint}</p>
                    <div className="mt-2 border-b border-dotted border-ink/40" />
                    <div className="mt-3 border-b border-dotted border-ink/25" />
                  </div>
                ))}
              </div>

              <p className="mt-8 text-center font-sans text-[8px] tracking-[0.4em] text-ink/40">
                INTELLIGENCE REQUIRED BEFORE MOVEMENT
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
